import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../../showcase/shared/ShowcasePageWrapper';
import {
  SLDSBarChart,
  SLDSDoughnutChart,
  MiniDonut,
  SLDS_CHART_COLORS,
  SLDS_CHART_COLORS_ALPHA,
} from '../../showcase/shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '16px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: COLORS.textPrimary,
  fontWeight: '400',
  marginBottom: '16px',
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------
const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block', backgroundColor: bgColor, color: color,
    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', lineHeight: '20px',
  }}>
    {label}
  </span>
);

const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? COLORS.brand : COLORS.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

const TimelineItem = ({ icon, iconColor, iconBg, title, description, timestamp, isLast }) => (
  <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
    {!isLast && (
      <div style={{ position: 'absolute', left: '15px', top: '32px', bottom: '-12px', width: '2px', backgroundColor: '#dddbda' }} />
    )}
    <div style={{
      width: '32px', height: '32px', borderRadius: '50%', backgroundColor: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1,
    }}>
      <Icon category="utility" name={icon} size="x-small" style={{ fill: iconColor }} />
    </div>
    <div style={{ flex: 1, paddingBottom: isLast ? '0' : '20px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#3e3e3c', marginBottom: '4px' }}>{description}</div>
      <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{timestamp}</div>
    </div>
  </div>
);

const QuickActionButton = ({ icon, label }) => (
  <button style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '12px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.border}`,
    borderRadius: '4px', cursor: 'pointer', width: '100%',
  }}>
    <Icon category="utility" name={icon} size="small" style={{ fill: COLORS.brand }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>{label}</span>
  </button>
);

// ---------------------------------------------------------------------------
// Custom DataTable Cells
// ---------------------------------------------------------------------------
const NameLinkCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: COLORS.brand, cursor: 'pointer', fontWeight: '500' }}>{children}</span>
  </DataTableCell>
);
NameLinkCell.displayName = DataTableCell.displayName;

const AmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
AmountCell.displayName = DataTableCell.displayName;

const TypeBadgeCell = ({ children, ...props }) => {
  const type = props.item && props.item.type;
  const typeColors = {
    'Cash': { bg: '#e8f5e9', color: '#2e844a', border: '#a8d8b0' },
    'Stock': { bg: '#eef4ff', color: '#0176d3', border: '#b9d6f7' },
    'In-Kind': { bg: '#f0e6fb', color: '#9050e9', border: '#d4b8f0' },
    'Pledge': { bg: '#fef3e8', color: '#fe9339', border: '#fdd5a0' },
    'Grant': { bg: '#e6f7f3', color: '#06a59a', border: '#a8e5de' },
    'DAF': { bg: '#fef1f1', color: '#c23934', border: '#f4b4b1' },
  };
  const colorSet = typeColors[type] || { bg: '#f3f3f3', color: '#706e6b', border: '#dddbda' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color, border: `1px solid ${colorSet.border}`,
      }}>
        {type}
      </span>
    </DataTableCell>
  );
};
TypeBadgeCell.displayName = DataTableCell.displayName;

const StatusBadgeCell = ({ children, ...props }) => {
  const status = props.item && props.item.status;
  const statusColors = {
    'Posted': { bg: '#e8f5e9', color: '#2e844a' },
    'Pledged': { bg: '#eef4ff', color: '#0176d3' },
    'Pending': { bg: '#fef3e8', color: '#fe9339' },
  };
  const colorSet = statusColors[status] || { bg: '#f3f3f3', color: '#706e6b' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color,
      }}>
        {status}
      </span>
    </DataTableCell>
  );
};
StatusBadgeCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '4px',
      marginBottom: '16px', display: 'flex', alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button key={tab} onClick={() => onTabChange(tab)} style={{
          padding: '12px 24px', fontSize: '14px',
          fontWeight: activeTab === tab ? '700' : '400',
          color: activeTab === tab ? COLORS.brand : '#3e3e3c',
          backgroundColor: 'transparent', border: 'none',
          borderBottom: activeTab === tab ? `3px solid ${COLORS.brand}` : '3px solid transparent',
          cursor: 'pointer',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const NPSPAccount = () => {
  const [activeNav, setActiveNav] = useState('Record Home');
  const [relatedTab, setRelatedTab] = useState(0);
  const [timelineExpanded, setTimelineExpanded] = useState(true);
  const [isMailchimpSubscribed, setIsMailchimpSubscribed] = useState(true);

  // Giving by FY bar chart
  const givingByFYData = useMemo(() => ({
    labels: ['FY19', 'FY20', 'FY21', 'FY22', 'FY23', 'FY24', 'FY25'],
    datasets: [{
      label: 'Giving',
      data: [18000, 24000, 32000, 28000, 45000, 38000, 42000],
      backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
      borderColor: SLDS_CHART_COLORS[0],
      borderWidth: 1.5, borderRadius: 3, barPercentage: 0.7,
    }],
  }), []);

  const givingByFYOptions = useMemo(() => ({
    plugins: { legend: { display: false } },
    scales: { y: { ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` } } },
  }), []);

  const givingBreakdownData = useMemo(() => ({
    labels: ['Cash', 'Stock/Securities', 'In-Kind', 'Pledge'],
    datasets: [{
      data: [145000, 72000, 38000, 32000],
      backgroundColor: [SLDS_CHART_COLORS[0], SLDS_CHART_COLORS[1], SLDS_CHART_COLORS[2], SLDS_CHART_COLORS[4]],
      borderColor: COLORS.white, borderWidth: 3,
    }],
  }), []);

  const givingBreakdownOptions = useMemo(() => ({
    cutout: '55%',
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 12, boxWidth: 12 } },
    },
  }), []);

  // Data
  const contacts = useMemo(() => [
    { id: 'c-1', name: 'David Martinez', role: 'Executive Director', email: 'dmartinez@gbafoodbank.org', phone: '(415) 555-0101' },
    { id: 'c-2', name: 'Sarah Kim', role: 'Development Director', email: 'skim@gbafoodbank.org', phone: '(415) 555-0102' },
    { id: 'c-3', name: 'James Wilson', role: 'Board Chair', email: 'jwilson@gbafoodbank.org', phone: '(415) 555-0103' },
    { id: 'c-4', name: 'Lisa Chen', role: 'Program Manager', email: 'lchen@gbafoodbank.org', phone: '(415) 555-0104' },
    { id: 'c-5', name: 'Michael Brown', role: 'Finance Director', email: 'mbrown@gbafoodbank.org', phone: '(415) 555-0105' },
    { id: 'c-6', name: 'Jennifer Adams', role: 'Volunteer Coordinator', email: 'jadams@gbafoodbank.org', phone: '(415) 555-0106' },
  ], []);

  const affiliations = useMemo(() => [
    { id: 'a-1', name: 'Bay Area Nonprofit Alliance', role: 'Member', since: '2015' },
    { id: 'a-2', name: 'Feeding America', role: 'Network Partner', since: '2012' },
    { id: 'a-3', name: 'United Way of the Bay Area', role: 'Grantee', since: '2018' },
  ], []);

  const donationHistory = useMemo(() => [
    { id: 'd-1', date: '2025-12-28', amount: 25000, type: 'Cash', campaign: 'Year-End Appeal 2025', status: 'Posted', designation: 'General Operating' },
    { id: 'd-2', date: '2025-09-15', amount: 10000, type: 'Stock', campaign: 'Fall Fundraiser', status: 'Posted', designation: 'Capital Campaign' },
    { id: 'd-3', date: '2025-06-01', amount: 5000, type: 'Cash', campaign: 'Spring Gala 2025', status: 'Posted', designation: 'Scholarship Fund' },
    { id: 'd-4', date: '2025-03-15', amount: 2000, type: 'Cash', campaign: 'Monthly Giving', status: 'Posted', designation: 'General Operating' },
    { id: 'd-5', date: '2024-12-20', amount: 75000, type: 'Stock', campaign: 'Year-End Appeal 2024', status: 'Posted', designation: 'Capital Campaign' },
    { id: 'd-6', date: '2024-09-01', amount: 15000, type: 'DAF', campaign: 'Annual Fund', status: 'Posted', designation: 'General Operating' },
    { id: 'd-7', date: '2024-06-15', amount: 8000, type: 'Cash', campaign: 'Spring Gala 2024', status: 'Posted', designation: 'Program Services' },
    { id: 'd-8', date: '2024-03-01', amount: 5000, type: 'In-Kind', campaign: 'Food Drive 2024', status: 'Posted', designation: 'Program Services' },
    { id: 'd-9', date: '2025-11-01', amount: 12000, type: 'Pledge', campaign: 'Capital Campaign', status: 'Pledged', designation: 'Capital Campaign' },
    { id: 'd-10', date: '2025-10-15', amount: 3000, type: 'Grant', campaign: 'Community Grant', status: 'Pending', designation: 'Program Services' },
  ], []);

  const activities = useMemo(() => [
    { icon: 'call', iconColor: COLORS.success, iconBg: '#e8f5e9', title: 'Call with Sarah Kim', description: 'Discussed Q1 program outcomes and upcoming Year-End Appeal strategy. Confirmed $25K gift commitment.', timestamp: 'Today at 2:30 PM' },
    { icon: 'email', iconColor: COLORS.brand, iconBg: '#e6f2fb', title: 'Email: Impact Report Sent', description: 'Sent 2025 Annual Impact Report to David Martinez and board members.', timestamp: 'Yesterday at 4:15 PM' },
    { icon: 'event', iconColor: COLORS.purple, iconBg: '#f0e6fb', title: 'Meeting: Site Visit', description: 'Board members toured new distribution center. Discussed expansion plans.', timestamp: 'Dec 20, 2025 at 10:00 AM' },
    { icon: 'task', iconColor: COLORS.warning, iconBg: '#fef3e8', title: 'Task: Update Gift Records', description: 'Updated Q4 gift entries and reconciled stock transfer.', timestamp: 'Dec 18, 2025 at 11:30 AM' },
    { icon: 'note', iconColor: COLORS.textSecondary, iconBg: COLORS.bg, title: 'Note: Grant Renewal', description: 'United Way grant renewal due Feb 15. Need outcomes report.', timestamp: 'Dec 15, 2025 at 3:00 PM' },
  ], []);

  const programs = useMemo(() => [
    { name: 'Emergency Food Distribution', status: 'Active', since: '2019' },
    { name: 'Holiday Meal Program', status: 'Active', since: '2020' },
    { name: 'School Backpack Program', status: 'Active', since: '2021' },
    { name: 'Community Garden Initiative', status: 'Completed', since: '2022' },
  ], []);

  return (
    <ShowcasePageWrapper
      title="Greater Bay Area Food Bank"
      description="Nonprofit organization record page"
      icon="account"
      iconCategory="standard"
    >
      {/* Record Header */}
      <div style={{ ...cardStyle }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#7F8DE1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon category="standard" name="account" size="medium" style={{ fill: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Account
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary, margin: 0 }}>
                  Greater Bay Area Food Bank
                </h1>
                <Badge label="Organization" color={COLORS.white} bgColor={COLORS.purple} />
                <Badge label="Leadership Circle" color={COLORS.white} bgColor={COLORS.success} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button label="Edit" variant="neutral" />
            <Button label="Clone" variant="neutral" />
            <Button label="Log Activity" variant="neutral" iconCategory="utility" iconName="log_a_call" iconPosition="left" />
            <Button label="New Donation" variant="brand" iconCategory="utility" iconName="add" iconPosition="left" />
          </div>
        </div>

        {/* Compact Layout - Key Fields */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '24px',
          paddingTop: '16px',
          borderTop: `1px solid ${COLORS.border}`
        }}>
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px', fontWeight: '400' }}>
              Total Gifts
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>
              35
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px', fontWeight: '400' }}>
              Years as Donor
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>
              7
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px', fontWeight: '400' }}>
              Soft Credits
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>
              $35,000
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px', fontWeight: '400' }}>
              Consecutive Years
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>
              7
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px', fontWeight: '400' }}>
              Last Gift Date
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>
              Dec 28, 2025
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Three-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '25% 50% 25%', gap: '16px', alignItems: 'start' }}>

        {/* LEFT COLUMN — Highlights */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Giving Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Lifetime Giving', value: '$287,000', icon: 'moneybag', color: COLORS.success },
                { label: 'This Year', value: '$42,000', icon: 'date_input', color: COLORS.brand },
                { label: 'Largest Gift', value: '$75,000', icon: 'trending', color: COLORS.purple },
                { label: 'Average Gift', value: '$8,250', icon: 'chart', color: COLORS.teal },
              ].map(m => (
                <div key={m.label} style={{ backgroundColor: COLORS.bg, borderRadius: '4px', padding: '12px', textAlign: 'center' }}>
                  <Icon category="utility" name={m.icon} size="small" style={{ fill: m.color, marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.textPrimary }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Engagement Score */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textPrimary }}>Engagement Score</div>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>Last 90 days</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: COLORS.success, lineHeight: 1 }}>87</div>
                  <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginTop: '2px' }}>
                    <Icon category="utility" name="up" size="xx-small" style={{ fill: COLORS.success }} />
                    <span style={{ color: COLORS.success, fontWeight: '600', marginLeft: '2px' }}>+5</span> vs last period
                  </div>
                </div>
              </div>

              {/* Category Breakdown */}
              <div style={{ marginBottom: '12px' }}>
                {[
                  {
                    label: 'Email Engagement',
                    value: 92,
                    icon: 'email',
                    color: COLORS.brand,
                    details: [
                      { metric: 'Open Rate', value: '78%', benchmark: '45% avg' },
                      { metric: 'Click Rate', value: '34%', benchmark: '12% avg' },
                      { metric: 'Emails Opened', value: '47/52' },
                    ]
                  },
                  {
                    label: 'Event Attendance',
                    value: 85,
                    icon: 'event',
                    color: COLORS.purple,
                    details: [
                      { metric: 'Events Attended', value: '6/8 invited' },
                      { metric: 'Last Event', value: 'Holiday Gala' },
                      { metric: 'Avg. Guests', value: '2.3' },
                    ]
                  },
                  {
                    label: 'Website Activity',
                    value: 88,
                    icon: 'world',
                    color: COLORS.teal,
                    details: [
                      { metric: 'Page Visits', value: '43', benchmark: '8x avg' },
                      { metric: 'Time on Site', value: '12m 34s' },
                      { metric: 'Articles Read', value: '17' },
                    ]
                  },
                  {
                    label: 'Donation Activity',
                    value: 95,
                    icon: 'moneybag',
                    color: COLORS.success,
                    details: [
                      { metric: 'Frequency', value: 'Monthly' },
                      { metric: 'Consistency', value: '7 years' },
                      { metric: 'Growth', value: '+15% YoY' },
                    ]
                  },
                ].map((category, idx) => (
                  <details key={category.label} style={{ marginBottom: '8px' }}>
                    <summary style={{
                      cursor: 'pointer',
                      listStyle: 'none',
                      padding: '8px',
                      backgroundColor: COLORS.bg,
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8e8e8'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.bg}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon category="utility" name={category.icon} size="x-small" style={{ fill: category.color }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: COLORS.textPrimary }}>{category.label}</span>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: category.color }}>{category.value}</span>
                          </div>
                          <div style={{ height: '4px', backgroundColor: '#e5e5e5', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{
                              width: `${category.value}%`,
                              height: '100%',
                              backgroundColor: category.color,
                              borderRadius: '2px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                        <Icon
                          category="utility"
                          name="chevronright"
                          size="xx-small"
                          style={{ fill: COLORS.textSecondary, transition: 'transform 0.2s' }}
                          className="chevron-icon"
                        />
                      </div>
                    </summary>

                    {/* Expanded Details */}
                    <div style={{
                      marginTop: '8px',
                      marginLeft: '28px',
                      paddingLeft: '12px',
                      borderLeft: `2px solid ${category.color}`,
                    }}>
                      {category.details.map((detail, detailIdx) => (
                        <div key={detailIdx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                          fontSize: '11px'
                        }}>
                          <span style={{ color: COLORS.textSecondary }}>{detail.metric}</span>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontWeight: '600', color: COLORS.textPrimary }}>{detail.value}</span>
                            {detail.benchmark && (
                              <span style={{
                                fontSize: '10px',
                                color: COLORS.success,
                                marginLeft: '6px',
                                backgroundColor: '#e8f5e9',
                                padding: '1px 4px',
                                borderRadius: '2px'
                              }}>
                                {detail.benchmark}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>

              {/* Overall Summary */}
              <div style={{
                backgroundColor: '#f0fff4',
                border: `1px solid #c6f6d5`,
                borderRadius: '4px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon category="utility" name="success" size="x-small" style={{ fill: COLORS.success }} />
                <span style={{ fontSize: '11px', color: '#22543d', fontWeight: '500' }}>
                  Highly Engaged Donor - Top 10% of all accounts
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Detail + Related */}
        <div>
          {/* Account Information */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Account Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="Account Name" value="Greater Bay Area Food Bank" isLink />
              <FieldDisplay label="Account Type" value="Organization" />
              <FieldDisplay label="Organization Type" value="501(c)(3) Nonprofit" />
              <FieldDisplay label="Industry/Focus" value="Food & Hunger Relief" />
              <FieldDisplay label="Phone" value="(415) 555-0100" />
              <FieldDisplay label="Website" value="www.gbafoodbank.org" isLink />
              <FieldDisplay label="EIN / Tax ID" value="94-2847501" />
              <FieldDisplay label="Giving Level" value="Leadership Circle" />
              <div style={{ marginBottom: '16px' }}>
                <div style={fieldLabelStyle}>Account Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.brand,
                    color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700',
                  }}>JA</div>
                  <span style={{ fontSize: '14px', color: COLORS.brand, cursor: 'pointer' }}>Jennifer Adams</span>
                </div>
              </div>
              <FieldDisplay label="Donor Since" value="January 2018" />
              <div style={{ gridColumn: '1 / -1' }}>
                <FieldDisplay label="Description" value="Greater Bay Area Food Bank distributes over 2.3 million meals annually to families in need across the San Francisco Bay Area. They partner with 180 community organizations, schools, and shelters to fight hunger and food insecurity." />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <FieldDisplay label="Billing Address" value="1200 Howard Street, Suite 300, San Francisco, CA 94103" />
              </div>
            </div>
          </div>

          {/* Related Lists */}
          <div style={cardStyle}>
            <Tabs id="account-related-tabs" selectedIndex={relatedTab} onSelect={(index) => setRelatedTab(index)}>
              <TabsPanel label={`Contacts (${contacts.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={contacts} id="contacts-table" fixedLayout>
                    <DataTableColumn label="Name" property="name" width="200px">
                      <NameLinkCell />
                    </DataTableColumn>
                    <DataTableColumn label="Role" property="role" width="180px" />
                    <DataTableColumn label="Email" property="email" width="240px" />
                    <DataTableColumn label="Phone" property="phone" width="150px" />
                  </DataTable>
                  <div style={{ textAlign: 'center', marginTop: '12px' }}>
                    <Button label="View All Contacts" variant="base" />
                  </div>
                </div>
              </TabsPanel>
              <TabsPanel label={`Donations (${donationHistory.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={donationHistory} id="donation-history-table" fixedLayout>
                    <DataTableColumn label="Date" property="date" width="100px" />
                    <DataTableColumn label="Amount" property="amount" width="110px">
                      <AmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Type" property="type" width="100px">
                      <TypeBadgeCell />
                    </DataTableColumn>
                    <DataTableColumn label="Campaign" property="campaign" width="170px" />
                    <DataTableColumn label="Status" property="status" width="90px">
                      <StatusBadgeCell />
                    </DataTableColumn>
                  </DataTable>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${COLORS.border}` }}>
                    <Button label="View All Donations" variant="base" />
                    <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      Total: <strong style={{ color: COLORS.textPrimary }}>${donationHistory.reduce((sum, d) => sum + d.amount, 0).toLocaleString()}</strong>
                    </span>
                  </div>
                </div>
              </TabsPanel>
              <TabsPanel label={`Affiliations (${affiliations.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={affiliations} id="affiliations-table" fixedLayout>
                    <DataTableColumn label="Organization" property="name" width="280px">
                      <NameLinkCell />
                    </DataTableColumn>
                    <DataTableColumn label="Role" property="role" width="200px" />
                    <DataTableColumn label="Since" property="since" width="100px" />
                  </DataTable>
                </div>
              </TabsPanel>
              <TabsPanel label={`Programs (${programs.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {programs.map((prog, idx) => (
                    <div key={idx} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 0', borderBottom: idx < programs.length - 1 ? `1px solid ${COLORS.bg}` : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: COLORS.textPrimary }}>{prog.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Since {prog.since}</div>
                      </div>
                      <Badge
                        label={prog.status}
                        color={prog.status === 'Active' ? COLORS.success : COLORS.textSecondary}
                        bgColor={prog.status === 'Active' ? '#e8f5e9' : COLORS.bg}
                      />
                    </div>
                  ))}
                </div>
              </TabsPanel>
            </Tabs>
          </div>

          {/* Activity Timeline */}
          <div style={cardStyle}>
            <ExpandableSection title="Activity Timeline" isOpen={timelineExpanded} onToggleOpen={() => setTimelineExpanded(!timelineExpanded)}>
              <div style={{ padding: '8px 0' }}>
                {activities.map((a, idx) => (
                  <TimelineItem key={idx} icon={a.icon} iconColor={a.iconColor} iconBg={a.iconBg}
                    title={a.title} description={a.description} timestamp={a.timestamp} isLast={idx === activities.length - 1} />
                ))}
              </div>
            </ExpandableSection>
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
              <Button label="View All Activity" variant="base" />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div>
          {/* Quick Actions */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <QuickActionButton icon="call" label="Log a Call" />
              <QuickActionButton icon="email" label="Send Email" />
              <QuickActionButton icon="task" label="New Task" />
              <QuickActionButton icon="event" label="New Event" />
              <QuickActionButton icon="note" label="New Note" />
              <QuickActionButton icon="opportunity" label="New Donation" />
            </div>
          </div>

          {/* Mailchimp Subscription Status */}
          <div style={{
            ...cardStyle,
            backgroundColor: isMailchimpSubscribed ? '#e8f5e9' : '#fef1f1',
            borderColor: isMailchimpSubscribed ? '#81c784' : '#f4b4b1',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '6px',
                backgroundColor: COLORS.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c.68 0 1.34-.08 1.99-.22.64-.14 1.11-.72 1.11-1.39v-.61c0-.37-.15-.73-.42-.99-.26-.25-.4-.6-.4-.97 0-.74.6-1.34 1.34-1.34h1.57c3.08 0 5.58-2.5 5.58-5.58C22.77 6.48 18.29 2 12 2z" fill="#FFE01B"/>
                  <circle cx="7" cy="10" r="1.5" fill="#241C15"/>
                  <circle cx="12" cy="7" r="1.5" fill="#241C15"/>
                  <circle cx="17" cy="10" r="1.5" fill="#241C15"/>
                  <circle cx="9" cy="15" r="1.5" fill="#241C15"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', margin: 0, color: COLORS.textPrimary }}>
                  Mailchimp
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isMailchimpSubscribed ? COLORS.success : COLORS.error
                  }} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: isMailchimpSubscribed ? COLORS.success : COLORS.error
                  }}>
                    {isMailchimpSubscribed ? 'Subscribed' : 'Unsubscribed'}
                  </span>
                </div>
              </div>
            </div>

            {isMailchimpSubscribed ? (
              <>
                {/* Subscribed State - Show Engagement Stats */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '8px', fontWeight: '600' }}>
                    Last 30 Days
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { label: 'Emails Sent', value: '12', icon: 'email' },
                      { label: 'Opens', value: '11', icon: 'preview' },
                      { label: 'Clicks', value: '8', icon: 'touch_action' },
                      { label: 'Open Rate', value: '92%', icon: 'trending' },
                    ].map(stat => (
                      <div key={stat.label} style={{
                        backgroundColor: COLORS.white,
                        borderRadius: '4px',
                        padding: '8px',
                        border: `1px solid ${COLORS.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          <Icon category="utility" name={stat.icon} size="xx-small" style={{ fill: COLORS.success }} />
                          <span style={{ fontSize: '10px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            {stat.label}
                          </span>
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.textPrimary }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lists */}
                <div style={{
                  backgroundColor: COLORS.white,
                  borderRadius: '4px',
                  padding: '10px',
                  marginBottom: '12px',
                  border: `1px solid ${COLORS.border}`
                }}>
                  <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: '600' }}>
                    Active Lists
                  </div>
                  {['Monthly Newsletter', 'Major Donor Updates'].map(list => (
                    <div key={list} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <Icon category="utility" name="check" size="xx-small" style={{ fill: COLORS.success }} />
                      <span style={{ fontSize: '12px', color: COLORS.textPrimary }}>{list}</span>
                    </div>
                  ))}
                </div>

                <Button
                  label="Unsubscribe"
                  variant="destructive"
                  onClick={() => setIsMailchimpSubscribed(false)}
                  style={{ width: '100%' }}
                  iconCategory="utility"
                  iconName="ban"
                  iconPosition="left"
                  iconSize="x-small"
                />
              </>
            ) : (
              <>
                {/* Unsubscribed State */}
                <div style={{
                  backgroundColor: COLORS.white,
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '12px',
                  border: `1px solid ${COLORS.border}`,
                  textAlign: 'center'
                }}>
                  <Icon category="utility" name="warning" size="small" style={{ fill: COLORS.error, marginBottom: '8px' }} />
                  <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.4' }}>
                    This account is not receiving email communications. They will miss important updates and impact reports.
                  </div>
                </div>

                <div style={{
                  fontSize: '11px',
                  color: COLORS.textSecondary,
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: COLORS.white,
                  borderRadius: '4px',
                  border: `1px solid ${COLORS.border}`
                }}>
                  <strong style={{ color: COLORS.textPrimary }}>Unsubscribed:</strong> Dec 10, 2025<br/>
                  <strong style={{ color: COLORS.textPrimary }}>Reason:</strong> Too many emails
                </div>

                <Button
                  label="Resubscribe to Mailchimp"
                  variant="brand"
                  onClick={() => setIsMailchimpSubscribed(true)}
                  style={{ width: '100%' }}
                  iconCategory="utility"
                  iconName="add"
                  iconPosition="left"
                  iconSize="x-small"
                />
              </>
            )}

            {/* Footer Link */}
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: `1px solid ${isMailchimpSubscribed ? '#c8e6c9' : '#ffcdd2'}`,
              textAlign: 'center'
            }}>
              <a href="#" style={{
                fontSize: '11px',
                color: COLORS.brand,
                textDecoration: 'none',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                View in Mailchimp
                <Icon category="utility" name="new_window" size="xx-small" style={{ fill: COLORS.brand }} />
              </a>
            </div>
          </div>

          {/* Giving Trend */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Giving by Fiscal Year
            </div>
            <SLDSBarChart data={givingByFYData} options={givingByFYOptions} height={160} />
          </div>

          {/* Giving Breakdown */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Giving by Type
            </div>
            <SLDSDoughnutChart data={givingBreakdownData} options={givingBreakdownOptions} height={180} />
          </div>

          {/* Soft Credits */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Soft Credits</h3>
            {[
              { donor: 'Margaret Morrison', amount: '$15,000', type: 'Board Member' },
              { donor: 'Robert Chen', amount: '$10,000', type: 'Solicitor' },
              { donor: 'Bay Area Tech Foundation', amount: '$10,000', type: 'Matching' },
            ].map((c, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < 2 ? `1px solid ${COLORS.bg}` : 'none' }}>
                <div>
                  <div style={{ fontSize: '13px', color: COLORS.brand, cursor: 'pointer' }}>{c.donor}</div>
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>{c.type}</div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>{c.amount}</span>
              </div>
            ))}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>Total</span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: COLORS.textPrimary }}>$35,000</span>
            </div>
          </div>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default NPSPAccount;
